import _ from 'lodash';
import Big from 'big.js';

import { hasSaleSpecialForMenuType } from 'shared/helpers/specials';
import { amortizeDollarDiscount, isCumulative } from './utilities';
import { sumProductTaxes } from './taxes';

export function couponApplicableCategoriesMatchProductDetail(coupon, detail) {
  return _.some(
    coupon?.applicableCategories,
    (categoryObject) =>
      categoryObject.category === detail.productCategory &&
      (categoryObject.subcategory === 'all' || categoryObject.subcategory === detail.productSubcategory)
  );
}

export const couponIsApplicable = (detail, coupon) => {
  if (!coupon || (detail.isOnSale && coupon?.restrictions?.noSpecials)) {
    return false;
  }
  if (!_.isEmpty(coupon?.applicableCategories)) {
    return couponApplicableCategoriesMatchProductDetail(coupon, detail);
  }
  return true;
};

export const calculateCouponDiscounts = ({
  applyStowedDiscounts,
  compositePrice,
  coupon,
  couponApplicableSubtotal,
  couponApplicableSubtotalPlusTaxes,
  couponLeftOver,
  credit,
  detail,
  discountTaxOrder,
  originalCompositePrice,
  productTotal,
  receiptDetail,
  globalSpecialsSettings,
  stowDiscounts,
  stowedDiscounts,
}) => {
  const couponToApply = (() => {
    if (applyStowedDiscounts) {
      const couponDiscount = _.find(stowedDiscounts, ['type', 'coupon']);
      return couponDiscount
        ? { ...couponDiscount.discountData, _quantityToApply: couponDiscount.quantityToApply }
        : null;
    }
    return coupon;
  })();

  if (couponIsApplicable(detail, couponToApply)) {
    const stackingBehaviorIsCumulative = isCumulative(null, globalSpecialsSettings.stackingBehavior);
    const startingPrice = stackingBehaviorIsCumulative ? originalCompositePrice : compositePrice;

    const individualPrice = startingPrice.div(detail.quantity);
    const quantityToApply = !_.isNil(couponToApply?._quantityToApply)
      ? couponToApply._quantityToApply
      : detail.quantity || 0;
    let additionalCredit = Big(0);

    if (couponToApply?.percent) {
      additionalCredit = Big(individualPrice.times(Big(couponToApply.percent)).times(quantityToApply));

      if (!stowDiscounts) {
        if (discountTaxOrder === 'discountsFirst') {
          compositePrice = compositePrice.minus(additionalCredit);
        }

        if (discountTaxOrder !== 'discountsFirst' || Big(detail.basePriceMult, 'Base Price Mult').eq(1)) {
          credit = credit.plus(additionalCredit);
        } else {
          productTotal = productTotal.minus(additionalCredit);
        }
      }
    } else if (couponToApply?.dollar) {
      // TODO: consider a better approach for dollar discount coupons in the no-stacking case when combined with a BOGO
      let couponData = couponToApply;
      if (discountTaxOrder === 'taxesFirst') {
        const maxDollarDiscount = Big(Math.min(Number(couponApplicableSubtotalPlusTaxes), couponToApply?.dollar || 0));
        couponData = { dollar: Number(maxDollarDiscount) };
      }

      const { amortizedDiscount, leftOver } = amortizeDollarDiscount(
        couponData,
        couponLeftOver,
        discountTaxOrder === 'taxesFirst' ? couponApplicableSubtotalPlusTaxes : couponApplicableSubtotal,
        originalCompositePrice
      );

      /*
        if the amortized discount is greater than the discounted product price
        we want to try and roll the remaining coupon discount to the next item
       */
      if (amortizedDiscount.gt(compositePrice)) {
        // add remaining coupon discount to next left over
        couponLeftOver = amortizedDiscount.minus(compositePrice);
        // add as much as we can to this product
        additionalCredit = compositePrice;
      } else {
        additionalCredit = Big(amortizedDiscount);
      }

      if (!stowDiscounts) {
        couponLeftOver.plus(leftOver);

        if (discountTaxOrder === 'taxesFirst') {
          credit = credit.plus(additionalCredit);
        } else {
          compositePrice = compositePrice.minus(additionalCredit);
          productTotal = productTotal.minus(additionalCredit);
        }
      }
    }

    if (stowDiscounts) {
      stowedDiscounts.push({
        applicableQuantity: detail.quantity,
        discountData: couponToApply,
        individualDiscount: additionalCredit.div(detail.quantity),
        key: couponToApply._id,
        type: 'coupon',
      });
    } else if (receiptDetail && additionalCredit.gt(0)) {
      receiptDetail.addDiscount({
        type: 'coupon',
        id: couponToApply._id,
        name: couponToApply.code,
        value: additionalCredit.times(100),
      });
    }
  }

  return { compositePrice, couponLeftOver, credit, productTotal, stowedDiscounts };
};

export function productDetailHasSpecialsConflicts(detail, products, medicalOrder, coupon) {
  return (
    hasSaleSpecialForMenuType({
      product: products[`${detail.id}_${detail.option}`].product,
      medicalOrder,
    }) && coupon?.restrictions?.noSpecials
  );
}

export function getCouponApplicableSubtotal(details, products, medicalOrder, coupon) {
  return _.reduce(
    details,
    (accum, detail) => {
      const {
        specialAndCouponRestrictionConflict,
        couponAcceptsAllCategoriesOrMatchesRestriction,
      } = couponAppliesToProduct(detail, products, medicalOrder, coupon);
      return accum.add(
        !specialAndCouponRestrictionConflict && couponAcceptsAllCategoriesOrMatchesRestriction
          ? (detail.mixAndMatch?.adjustedBasePrice || detail.basePrice).times(detail.quantity)
          : 0
      );
    },
    Big(0)
  );
}

export const prepareCoupon = ({ couponValue, details, discountTaxOrder, medicalOrder, products }) => {
  let coupon;

  if (couponValue) {
    if (_.isArray(couponValue)) {
      [coupon] = couponValue;
    } else {
      coupon = { ...couponValue };
    }
  }

  const couponApplicableSubtotal = getCouponApplicableSubtotal(details, products, medicalOrder, coupon);

  // TODO: consider doing this here, so couponApplicableSubtotalPlusTaxes isn't needed (tests would need updated)
  // Add total taxes to couponApplicableSubtotal for taxesFirst setting
  // if (discountTaxOrder === 'taxesFirst') {
  //   couponApplicableSubtotal = couponApplicableSubtotal.add(gatherProductTaxes(details, products));
  // }

  // If dollar discount totals more than possible subtotal swap the dollar value
  if (coupon?.dollar && (discountTaxOrder === 'both' || discountTaxOrder === 'discountsFirst')) {
    coupon.dollar = Math.min(Number(couponApplicableSubtotal), coupon.dollar);
  }

  return {
    coupon,
    couponApplicableSubtotal,
    couponApplicableSubtotalPlusTaxes: couponApplicableSubtotal.plus(
      gatherTaxesForCouponApplicableProducts(details, products, medicalOrder, coupon)
    ),
    couponLeftOver: Big(0),
  };
};

function couponAppliesToProduct(detail, products, medicalOrder, coupon) {
  const specialAndCouponRestrictionConflict = productDetailHasSpecialsConflicts(detail, products, medicalOrder, coupon);
  let couponAcceptsAllCategoriesOrMatchesRestriction = true;
  if (!_.isEmpty(coupon?.applicableCategories)) {
    couponAcceptsAllCategoriesOrMatchesRestriction = couponApplicableCategoriesMatchProductDetail(coupon, detail);
  }
  return { specialAndCouponRestrictionConflict, couponAcceptsAllCategoriesOrMatchesRestriction };
}

function gatherTaxesForCouponApplicableProducts(details, products, medicalOrder, coupon) {
  return _.reduce(
    details,
    (accum, detail) => {
      const {
        specialAndCouponRestrictionConflict,
        couponAcceptsAllCategoriesOrMatchesRestriction,
      } = couponAppliesToProduct(detail, products, medicalOrder, coupon);

      let totalProductTax = Big(0);
      if (!specialAndCouponRestrictionConflict && couponAcceptsAllCategoriesOrMatchesRestriction) {
        const productTaxes = sumProductTaxes(detail, products, Big(0), Big(0));
        totalProductTax = totalProductTax.plus(productTaxes.cannabisTaxTotal).plus(productTaxes.salesTaxTotal);
      }
      return accum.add(totalProductTax);
    },
    Big(0)
  );
}
